import { fetchAuthSession } from 'aws-amplify/auth'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type React from 'react'

import { Icon, type IconName } from '@components/icons/Icon'
import { IconButton } from '@components/ui/IconButton'
import { AnimatePresence, motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useWallet } from '../providers/WalletProvider'

type SettingsItem = {
  id: string
  label: string
  icon: IconName
  description?: string
  trailingIcon?: IconName | null
  onClick?: () => void | Promise<void>
}

type SettingsSection = {
  id: string
  title: string
  items: SettingsItem[]
}

export interface SettingsSheetProps {
  open: boolean
  onClose?: () => void
}

type SheetView = 'main' | 'mail'

const PREFERENCE_ITEMS: SettingsItem[] = [
  { id: 'notifications', label: '通知設定', icon: 'Notification', trailingIcon: 'ChevronRight' },
  { id: 'mail', label: 'メール配信設定', icon: 'Mail', trailingIcon: 'ChevronRight' },
  { id: 'backup', label: 'ウォレットバックアップ', icon: 'Download', trailingIcon: 'ChevronRight' },
]
// Keep the array referenced so TypeScript allows us to retain it while the section stays hidden
void PREFERENCE_ITEMS

const OTHER_ITEMS: SettingsItem[] = [
  // { id: 'sell-arcs', label: 'ARCSを売却', icon: 'Arcs', trailingIcon: 'Expand' },
  {
    id: 'contact',
    label: 'お問い合わせ',
    description: 'サポートデスクへお問い合わせください。',
    icon: 'Contact',
    trailingIcon: null,
  },
  { id: 'logout', label: 'ログアウト', icon: 'Logout', trailingIcon: null },
]

const CLOSE_BUTTON_ID = 'settings-sheet-close-button'
const TITLE_ID = 'settings-sheet-title'

export const SettingsSheet: React.FC<SettingsSheetProps> = ({ open, onClose }) => {
  const { logout } = useWallet()
  const navigate = useNavigate()
  const [view, setView] = useState<SheetView>('main')

  useEffect(() => {
    if (!open) {
      setView('main')
    }
  }, [open])

  const handleLogout = useCallback(async () => {
    await logout()
    navigate('/', { replace: true })
    onClose?.()
  }, [logout, navigate, onClose])

  useEffect(() => {
    if (!open) return

    const closeButton = document.getElementById(CLOSE_BUTTON_ID) as HTMLButtonElement | null
    closeButton?.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose?.()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  return (
    <AnimatePresence initial={false}>
      {open ? (
        <motion.div
          key="settings-sheet"
          className="fixed inset-0 z-50 flex w-full justify-center"
          aria-hidden={!open}
          style={{ pointerEvents: open ? 'auto' : 'none' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          <motion.button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="設定を閉じる"
            onClick={onClose}
            tabIndex={open ? 0 : -1}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          />
          <motion.dialog
            className="relative mt-auto mb-0 flex w-full max-w-[480px] justify-center border-0 bg-transparent px-0"
            open
            aria-labelledby={TITLE_ID}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 420, damping: 32, mass: 0.8 }}
          >
            <div className="flex w-full flex-col rounded-t-[32px] bg-white pb-12 pt-2 shadow-[0_-4px_16px_rgba(0,0,0,0.08)]">
              <div className="flex justify-center">
                <div className="h-1.5 w-12 rounded-full bg-zinc-300" aria-hidden />
              </div>
              <SheetHeader
                view={view}
                onBack={() => setView('main')}
                onClose={onClose}
                open={open}
              />
              <motion.div
                key={view}
                initial={{ opacity: 0, x: view === 'main' ? 20 : -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: view === 'main' ? -20 : 20 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                className="mt-6 flex flex-col gap-8 px-6"
              >
                {view === 'main' ? (
                  <MainContent
                    onSelectMail={() => setView('mail')}
                    onLogout={handleLogout}
                    open={open}
                  />
                ) : (
                  <MailContent open={open} />
                )}
              </motion.div>
            </div>
          </motion.dialog>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}

interface SheetHeaderProps {
  view: SheetView
  onBack: () => void
  onClose?: () => void
  open: boolean
}

const SheetHeader: React.FC<SheetHeaderProps> = ({ view, onBack, onClose, open }) => {
  const isMain = view === 'main'
  const title = isMain ? '設定' : 'メール配信設定'

  return (
    <header className="mt-3 flex items-center justify-between px-6">
      <div className="inline-flex h-10 w-10 items-center justify-center">
        {isMain ? null : (
          <IconButton aria-label="戻る" icon="Back" onClick={onBack} tabIndex={open ? 0 : -1} />
        )}
      </div>
      <p id={TITLE_ID} className="text-[20px] font-bold leading-none text-zinc-900">
        {title}
      </p>
      <IconButton
        id={CLOSE_BUTTON_ID}
        aria-label="設定を閉じる"
        icon="Close"
        onClick={onClose}
        tabIndex={open ? 0 : -1}
      />
    </header>
  )
}

const MainContent: React.FC<{
  onSelectMail: () => void
  onLogout: () => Promise<void>
  open: boolean
}> = ({ onSelectMail, onLogout, open }) => {
  // Retain onSelectMail for when the preferences section is re-enabled
  void onSelectMail

  const sections = useMemo<SettingsSection[]>(
    () => [
      // {
      //   id: 'preferences',
      //   title: '設定',
      //   items: PREFERENCE_ITEMS.map((item) =>
      //     item.id === 'mail' ? { ...item, onClick: onSelectMail } : item
      //   ),
      // },
      {
        id: 'others',
        title: 'その他',
        items: OTHER_ITEMS.map((item) =>
          item.id === 'logout' ? { ...item, onClick: onLogout } : item
        ),
      },
    ],
    [onLogout]
  )

  return (
    <>
      <PushNotificationSettings open={open} />
      {sections.map((section) => (
        <section key={section.id} className="flex flex-col gap-4">
          <p className="text-[14px] font-semibold tracking-[-0.41px] text-zinc-400">
            {section.title}
          </p>
          <div className="flex flex-col overflow-hidden bg-white divide-y divide-zinc-100">
            {section.items.map((item) => {
              const isInteractive = typeof item.onClick === 'function'
              const Component = isInteractive ? 'button' : 'div'

              return (
                <Component
                  key={item.id}
                  {...(isInteractive
                    ? {
                        type: 'button',
                        onClick: item.onClick,
                        tabIndex: open ? 0 : -1,
                      }
                    : {})}
                  className={[
                    'flex w-full items-center justify-between gap-3 py-4 text-left',
                    isInteractive
                      ? 'transition-colors duration-150 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-200 active:bg-zinc-100'
                      : 'cursor-default',
                  ].join(' ')}
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex size-10 items-center justify-center rounded-full bg-zinc-50">
                      <Icon content={item.icon} className="size-5" />
                    </span>
                    <span className="flex flex-col gap-1">
                      <span className="text-[14px] font-medium tracking-[-0.41px] text-zinc-900">
                        {item.label}
                      </span>
                      {item.description ? (
                        <span className="text-[12px] tracking-[-0.41px] text-zinc-400">
                          {item.description}
                        </span>
                      ) : null}
                    </span>
                  </div>
                  {item.trailingIcon !== undefined && item.trailingIcon !== null ? (
                    <Icon
                      content={item.trailingIcon}
                      className="size-5 shrink-0 opacity-40"
                      decorative
                    />
                  ) : null}
                </Component>
              )
            })}
          </div>
        </section>
      ))}

      <div className="mt-2 flex w-full flex-col items-center gap-2">
        <p className="text-[11px] font-semibold leading-[1.4] text-zinc-400">Ver. 1.0.0</p>
        <div className="h-px w-10 bg-zinc-400" />
        <p className="text-[10px] leading-[1.4] text-zinc-400">
          VENTUREWING MANAGEMENT CONSULTING L.L.C-FZ
        </p>
      </div>
    </>
  )
}

type PushStatus = 'checking' | 'subscribed' | 'not-subscribed' | 'unsupported' | 'error'

const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

const waitForServiceWorker = async (): Promise<ServiceWorkerRegistration> => {
  const resolveRegistration = async () => {
    const byScope = await navigator.serviceWorker.getRegistration('/')
    if (byScope) return byScope
    const defaultScope = await navigator.serviceWorker.getRegistration()
    if (defaultScope) return defaultScope
    const [first] = await navigator.serviceWorker.getRegistrations()
    return first
  }

  const registrationFromWindow = await window.__ARCS_SW_READY__
  if (registrationFromWindow) return registrationFromWindow

  const immediate = await resolveRegistration()
  if (immediate) return immediate

  return navigator.serviceWorker.ready
}

const getAuthToken = async (): Promise<string | null> => {
  try {
    const session = await fetchAuthSession()
    return session.tokens?.idToken?.toString() ?? null
  } catch (error) {
    console.error('[wallet-web] Failed to fetch Cognito ID token', error)
    return null
  }
}

const PushNotificationSettings: React.FC<{ open: boolean }> = ({ open }) => {
  const vapidKey = import.meta.env.VITE_WEB_PUSH_PUBLIC_KEY?.trim()
  const apiBaseUrl = (() => {
    const base = import.meta.env.VITE_WALLET_API_URL?.trim()
    if (!base) {
      throw new Error('VITE_WALLET_API_URL is not configured for push subscription')
    }
    return `${base.replace(/\/$/, '')}/user/push/subscriptions`
  })()
  const isPushSupported =
    typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window

  const [status, setStatus] = useState<PushStatus>('checking')
  const [isBusy, setIsBusy] = useState(false)
  const isSubscribed = status === 'subscribed'

  const refreshStatus = useCallback(async () => {
    if (!isPushSupported) {
      setStatus('unsupported')
      return
    }
    try {
      const registration = await waitForServiceWorker()
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        setStatus('subscribed')
      } else {
        setStatus('not-subscribed')
      }
    } catch (error) {
      console.error('[wallet-web] Failed to determine push subscription status', error)
      setStatus('error')
    }
  }, [isPushSupported])

  useEffect(() => {
    if (open) {
      void refreshStatus()
    }
  }, [open, refreshStatus])

  const handleSubscribe = useCallback(async () => {
    if (!isPushSupported) {
      setStatus('unsupported')
      return
    }
    if (!vapidKey) {
      setStatus('error')
      return
    }

    setIsBusy(true)
    try {
      const permissionResult = await Notification.requestPermission()
      if (permissionResult !== 'granted') {
        setStatus('not-subscribed')
        return
      }

      const registration = await waitForServiceWorker()
      const existing = await registration.pushManager.getSubscription()
      let subscription = existing
      if (!subscription) {
        const serverKey = urlBase64ToUint8Array(vapidKey) as Uint8Array<ArrayBuffer>
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: serverKey,
        })
      }

      if (!subscription) {
        setStatus('error')
        return
      }

      const token = await getAuthToken()
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) headers.Authorization = `Bearer ${token}`

      await fetch(apiBaseUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(subscription.toJSON()),
      })

      setStatus('subscribed')
    } catch (error) {
      console.error('[wallet-web] Failed to subscribe push notification', error)
      setStatus('error')
    } finally {
      setIsBusy(false)
    }
  }, [apiBaseUrl, isPushSupported])

  const handleUnsubscribe = useCallback(async () => {
    if (!isPushSupported) {
      setStatus('unsupported')
      return
    }
    setIsBusy(true)
    try {
      const registration = await waitForServiceWorker()
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        const token = await getAuthToken()
        const headers: HeadersInit = { 'Content-Type': 'application/json' }
        if (token) headers.Authorization = `Bearer ${token}`

        await fetch(apiBaseUrl, {
          method: 'DELETE',
          headers,
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        })

        await subscription.unsubscribe()
      }
      setStatus('not-subscribed')
    } catch (error) {
      console.error('[wallet-web] Failed to unsubscribe push notification', error)
      setStatus('error')
    } finally {
      setIsBusy(false)
    }
  }, [isPushSupported])

  const handleToggle = useCallback(() => {
    if (isSubscribed) {
      void handleUnsubscribe()
    } else {
      void handleSubscribe()
    }
  }, [handleSubscribe, handleUnsubscribe, isSubscribed])

  return (
    <section className="flex flex-col gap-3">
      <p className="text-[14px] font-semibold tracking-[-0.41px] text-zinc-400">通知</p>
      <div className="flex flex-col gap-3 bg-white py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="inline-flex size-10 items-center justify-center rounded-full bg-zinc-50">
              <Icon content="Notification" className="size-5" />
            </span>
            <div className="flex flex-col">
              <p className="text-[16px] font-semibold text-zinc-900">プッシュ通知</p>
              <p className="text-[12px] text-[#7b849b]">ブラウザ通知の購読・解除を設定します。</p>
            </div>
          </div>
          <label className="relative inline-flex cursor-pointer items-center">
            <input
              type="checkbox"
              className="peer sr-only"
              checked={isSubscribed}
              onChange={handleToggle}
              disabled={isBusy || status === 'unsupported'}
              tabIndex={open ? 0 : -1}
            />
            <span
              className={[
                'h-6 w-11 rounded-full transition-colors duration-200',
                isBusy || status === 'unsupported'
                  ? 'bg-[#d4d4d8]'
                  : 'peer-checked:bg-[#2859c5] bg-[#d4d4d8]',
              ].join(' ')}
            />
            <span
              className={[
                'absolute left-[4px] top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-white transition-transform duration-200',
                isSubscribed ? 'translate-x-[20px]' : '',
              ].join(' ')}
            />
          </label>
        </div>
        <div className="text-[12px] leading-relaxed text-[#7b849b]">
          {!vapidKey ? (
            <p className="mt-1 text-[#ef4444]">VITE_WEB_PUSH_PUBLIC_KEY を設定してください。</p>
          ) : null}
        </div>
      </div>
    </section>
  )
}

interface MailOption {
  id: string
  icon: IconName
  title: string
  description: string
  enabled: boolean
}

const MAIL_OPTIONS: MailOption[] = [
  {
    id: 'price-alert',
    icon: 'Alert',
    title: 'ARCS価格アラート',
    description: 'ARCSの価格が上昇・下落した際にアラートメールをお送りします。',
    enabled: true,
  },
  {
    id: 'project-update',
    icon: 'Feature',
    title: 'プロジェクトアップデート',
    description: 'ARCSプロジェクトに関するアップデートを定期的にお届けします。',
    enabled: false,
  },
]

const MailContent: React.FC<{ open: boolean }> = ({ open }) => {
  return (
    <div className="flex flex-col gap-4">
      {MAIL_OPTIONS.map((option) => (
        <MailOptionRow key={option.id} option={option} open={open} />
      ))}
    </div>
  )
}

const MailOptionRow: React.FC<{ option: MailOption; open: boolean }> = ({ option, open }) => {
  return (
    <div className="flex items-center justify-between gap-4 bg-white py-4">
      <div className="flex items-start gap-3">
        <span className="inline-flex size-10 items-center justify-center rounded-full bg-zinc-50">
          <Icon content={option.icon} className="size-5" />
        </span>
        <div className="flex flex-col gap-1">
          <p className="text-[16px] font-semibold text-zinc-900">{option.title}</p>
          <p className="text-[14px] text-[#7b849b]">{option.description}</p>
        </div>
      </div>
      <label className="relative inline-flex cursor-pointer items-center">
        <input
          type="checkbox"
          className="peer sr-only"
          checked={option.enabled}
          readOnly
          tabIndex={open ? 0 : -1}
        />
        <span className="h-6 w-11 rounded-full bg-[#d4d4d8] transition-colors duration-200 peer-checked:bg-[#2859c5]" />
        <span className="absolute left-[4px] top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-white transition-transform duration-200 peer-checked:translate-x-[20px]" />
      </label>
    </div>
  )
}

export default SettingsSheet
